// The pit lane as a path the car follows on its own: an open polyline from
// where the pit entry road leaves the track centreline to where the exit road
// rejoins it, plus where the speed limit, the pit boxes and the pit wall are,
// and the queries the game needs to hand control over and back.
//
// Distances along the pit path are called `d` (metres from the path start);
// lap distances on the track are `s`. Babylon-free.

import type { Track } from './track';
import { SEPANG_PIT_META, SEPANG_PIT_PATH } from './data/sepangPit';

/** Pit lane speed limit, km/h (FIA F1 Sporting Regulations B1.6.3a default). */
export const PIT_SPEED_LIMIT_KMH = 80;

export interface PitPose {
  x: number;
  z: number;
  /** Height of the pit lane surface, metres (same datum as the track elevation). */
  y: number;
  /** Heading in radians, 0 = +z (north), positive towards +x (east); as TrackFrame. */
  heading: number;
  /** Unit tangent (driving direction). */
  tx: number;
  tz: number;
  /** Gradient along the path (rise over run). */
  slope: number;
}

export interface PitProjection {
  /** Distance along the pit path, metres, clamped to [0, length]. */
  d: number;
  /** Signed lateral offset from the path, metres; positive = right of the driving direction. */
  offset: number;
  /** Distance from the path (unsigned, includes overshoot past either end). */
  distance: number;
  /** Segment start index (pass back as a hint). */
  index: number;
}

/** An open polyline with heights, measured like Track (frameAt / project). */
export class PitPath {
  readonly points: ReadonlyArray<{ x: number; z: number; y: number }>;
  /** Cumulative distance at each point; cum[last] = length. */
  readonly cum: Float64Array;
  readonly length: number;

  constructor(points: ReadonlyArray<{ x: number; z: number; y: number }>) {
    if (points.length < 2) throw new Error('PitPath needs at least 2 points');
    this.points = points;
    this.cum = new Float64Array(points.length);
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]!;
      const b = points[i]!;
      this.cum[i] = this.cum[i - 1]! + Math.hypot(b.x - a.x, b.z - a.z);
    }
    this.length = this.cum[points.length - 1]!;
  }

  get count(): number {
    return this.points.length;
  }

  private segmentAt(d: number): number {
    let lo = 0;
    let hi = this.points.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (this.cum[mid]! <= d) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /** Pose at distance d along the path (clamped to the ends). */
  frameAt(d: number): PitPose {
    const dd = Math.max(0, Math.min(this.length, d));
    const i = this.segmentAt(dd);
    const a = this.points[i]!;
    const b = this.points[i + 1]!;
    const seg = this.cum[i + 1]! - this.cum[i]! || 1;
    const t = (dd - this.cum[i]!) / seg;
    const tx = (b.x - a.x) / seg;
    const tz = (b.z - a.z) / seg;
    return {
      x: a.x + (b.x - a.x) * t,
      z: a.z + (b.z - a.z) * t,
      y: a.y + (b.y - a.y) * t,
      heading: Math.atan2(tx, tz),
      tx,
      tz,
      slope: (b.y - a.y) / seg,
    };
  }

  /** Closest point on the path. With a hint (a previous index), searches ±window segments only. */
  project(x: number, z: number, hint?: number, window = 30): PitProjection {
    const n = this.points.length;
    let best = { d2: Infinity, i: 0, t: 0 };
    const lo = hint === undefined ? 0 : Math.max(0, hint - window);
    const hi = hint === undefined ? n - 2 : Math.min(n - 2, hint + window);
    for (let i = lo; i <= hi; i++) {
      const a = this.points[i]!;
      const b = this.points[i + 1]!;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const l2 = dx * dx + dz * dz || 1;
      const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / l2));
      const d2 = (a.x + dx * t - x) ** 2 + (a.z + dz * t - z) ** 2;
      if (d2 < best.d2) best = { d2, i, t };
    }
    const a = this.points[best.i]!;
    const b = this.points[best.i + 1]!;
    const seg = this.cum[best.i + 1]! - this.cum[best.i]! || 1;
    const tx = (b.x - a.x) / seg;
    const tz = (b.z - a.z) / seg;
    const px = a.x + (b.x - a.x) * best.t;
    const pz = a.z + (b.z - a.z) * best.t;
    return {
      d: this.cum[best.i]! + seg * best.t,
      // Right-hand normal of the driving direction is (tz, -tx), as in Track.
      offset: (x - px) * tz - (z - pz) * tx,
      distance: Math.sqrt(best.d2),
      index: best.i,
    };
  }
}

/** A painted pit box (stop position) beside the garages. */
export interface PitBox {
  /** Centre of the box along the pit path, metres. */
  d: number;
  /** Lateral offset of the box centre from the path (positive = garage side). */
  offset: number;
  /** Box length along the lane and width across it, metres. */
  length: number;
  width: number;
  /** True for the player's box. */
  ours: boolean;
}

/** One cross-section of the pit surface: world points of its two edges. */
export interface PitCrossSection {
  d: number;
  left: { x: number; z: number };
  right: { x: number; z: number };
  /** True where the track-side edge (left when the pits are on the right) was cut back to the track's white line. */
  leftOnTrackEdge: boolean;
}

export interface PitLane {
  /** The driving line through the pit lane (open polyline with heights). */
  path: PitPath;
  /** Length of the pit path, metres. */
  length: number;
  /** Lap distance where the pit path leaves the track centreline (pit entry road starts). */
  entryS: number;
  /** Lap distance where the pit path rejoins the track centreline (end of the exit road). */
  exitS: number;
  /** Side of the track the pit lane is on: 1 = right of the driving direction, -1 = left. */
  entryOffsetSide: 1 | -1;
  /** Pit lane speed limit, km/h. */
  speedLimitKmh: number;
  /** Path distance of the pit entry line (speed limit starts) and the pit exit line (limit ends). */
  speedLimitStart: number;
  speedLimitEnd: number;
  /** Path distance of our pit box (where the car stops). */
  boxAt: number;
  /** Lateral offset (garage side, metres) of our stop position from the path. */
  boxOffset: number;
  /** All painted boxes, ours included. */
  boxes: PitBox[];
  /**
   * Pit building: [from, to] path distances; `frontOffset` is OSM's front face offset from the path;
   * the building is drawn straight, parallel to the track between lap distances fromS and toS, with
   * its front `trackOffset` metres from the centreline (on the pit side).
   */
  garages: { from: number; to: number; frontOffset: number; fromS: number; toS: number; trackOffset: number };
  /** Pit wall between the track and the pit lane: path distances it spans, height and thickness. */
  wall: { from: number; to: number; height: number; thickness: number };
  /** Entry road: path distance range where the player may commit, and the matching lap distances. */
  entry: { commitFromS: number; commitToS: number; roadLength: number };
  /**
   * Exit: `handbackD` is the first path distance after the pit wall where a car on the path is wholly
   * inside the track's white lines (`handbackS` on the lap); the path itself ends at `exitS`.
   */
  merge: { handbackD: number; handbackS: number; exitS: number };
  /** Height of the pit lane at path distance d. */
  heightAt(d: number): number;
  /** Pose on the path at distance d, optionally shifted sideways by `lateral` metres (positive = right). */
  poseAt(d: number, lateral?: number): PitPose;
  /** Sideways shift that takes the car from the path into our box and back out (0 away from the box). */
  boxLateralAt(d: number): number;
  /** Closest point on the pit path. */
  project(x: number, z: number, hint?: number): PitProjection;
  /** Distance from the path to the garage fronts (on the garage side) at path distance d. */
  garageFrontAt(d: number): number;
  /** Pit surface half-widths either side of the path before clipping at the track edge (left = track side). */
  halfWidthAt(d: number): { left: number; right: number };
  /** Track projection of the path point at distance d: lap distance and offset from the centreline. */
  trackOffsetAt(d: number): { s: number; offset: number };
  /** Surface cross-section at d, clipped so it never extends inside the track's white line. Null where the surface is all on the track. */
  crossSection(d: number): PitCrossSection | null;
  /** True if (x, z) is on the pit entry road (between the junction and the start of the pit lane proper). */
  isOnEntryRoad(x: number, z: number): boolean;
  /**
   * True once a car at track projection (s, offset) has committed to the pit entry: it is past the
   * junction, beyond the white line on the pit side, and on (or just beside) the entry road.
   */
  commitsToPit(s: number, offset: number): boolean;
  /** Track-side face of the pit wall as an offset from the centreline (on the pit side, positive), or undefined where there is no wall. */
  wallOffsetAt(s: number): number | undefined;
  /** True if (x, z) is on the pit surface or within `margin` metres of it (e.g. a vegetation keepout). */
  inFootprint(x: number, z: number, margin?: number): boolean;
}

// Layout constants (metres).
const ROAD_HALF_WIDTH = 3.5;
/** How far the pit surface reaches towards the track on the pit straight: far enough to meet the track edge. */
const STRAIGHT_LEFT = 14;
const WALL_THICKNESS = 0.5;
const WALL_HEIGHT = 1.05;
/** The pit wall's track-side face sits this far beyond the white line. */
const WALL_GAP = 0.35;
/** Speed limit lines relative to the pit building. */
const LIMIT_BEFORE_GARAGES = 50;
const LIMIT_AFTER_GARAGES = 60;
/** Team boxes start this fraction along the building (FIA/FOM garages at the entry end) and end here. */
const BOXES_FROM = 0.14;
const BOXES_TO = 0.98;
const BOX_COUNT = 11;
/** Our box: the 6th of 11, mid-pit lane. */
const OUR_BOX = 5;
/** Entry commit window, metres of path from the junction, and lateral tolerance beyond the road edge. */
const COMMIT_LENGTH = 80;
const COMMIT_TOLERANCE = 1.0;
/** Beyond the white line by this much before a car counts as heading for the pits. */
const COMMIT_EDGE_MARGIN = 0.5;

const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Signed lap-distance difference b - a in (-L/2, L/2]. */
const lapDelta = (a: number, b: number, L: number): number => {
  let d = (b - a) % L;
  if (d > L / 2) d -= L;
  if (d <= -L / 2) d += L;
  return d;
};

/**
 * Build a PitLane from a path (points with heights), the lap distances of its
 * junctions and the garage span. Exposed for tests and other circuits.
 */
export function makePitLane(
  track: Track,
  points: ReadonlyArray<{ x: number; z: number; y: number }>,
  meta: { entryS: number; exitS: number; garages: { from: number; to: number; frontOffset: number } },
): PitLane {
  const path = new PitPath(points);
  const L = path.length;
  const lapL = track.length;
  const n = path.count;

  // Track projection of every path point (monotone tracking from the entry junction).
  const projS = new Float64Array(n);
  const projOff = new Float64Array(n);
  const projIdx = new Int32Array(n);
  {
    let hint = track.project(points[0]!.x, points[0]!.z).index;
    for (let i = 0; i < n; i++) {
      const p = track.project(points[i]!.x, points[i]!.z, hint);
      hint = p.index;
      projS[i] = p.s;
      projOff[i] = p.offset;
      projIdx[i] = p.index;
    }
  }
  /** Track segment index near the path point at distance d (a search hint for Track.project). */
  const trackHintAt = (d: number): number => projIdx[Math.max(0, Math.min(n - 1, Math.round((d / L) * (n - 1))))]!;
  const atIndex = (arr: Float64Array, d: number, lap = false): number => {
    const dd = Math.max(0, Math.min(L, d));
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (path.cum[mid]! <= dd) lo = mid;
      else hi = mid;
    }
    const t = (dd - path.cum[lo]!) / (path.cum[hi]! - path.cum[lo]! || 1);
    const a = arr[lo]!;
    const b = arr[hi]!;
    if (!lap) return a + (b - a) * t;
    return (((a + lapDelta(a, b, lapL) * t) % lapL) + lapL) % lapL;
  };
  const trackOffsetAt = (d: number): { s: number; offset: number } => ({ s: atIndex(projS, d, true), offset: atIndex(projOff, d) });
  const side: 1 | -1 = atIndex(projOff, L / 2) >= 0 ? 1 : -1;

  const { garages } = meta;
  const speedLimitStart = Math.max(0, garages.from - LIMIT_BEFORE_GARAGES);
  const speedLimitEnd = Math.min(L, garages.to + LIMIT_AFTER_GARAGES);

  // Where the lane runs beside the track: from where the path, coming round from the
  // entry road, is within STRAIGHT_LEFT + a road half-width of the track edge, to the end.
  const edgeAt = (d: number): number => {
    const t = trackOffsetAt(d);
    const f = track.frameAt(t.s);
    return side > 0 ? f.widthRight : f.widthLeft;
  };
  let straightFrom = L;
  for (let d = COMMIT_LENGTH; d < L; d += 1) {
    if (side * trackOffsetAt(d).offset - edgeAt(d) < STRAIGHT_LEFT) {
      straightFrom = d;
      break;
    }
  }
  // The building is straight (parallel to the track); its front sits where OSM puts it relative to
  // the lane along the settled part of the pit straight, so the lane never narrows below that.
  const fromS = trackOffsetAt(garages.from).s;
  const toS = trackOffsetAt(garages.to).s;
  let frontTrack = 0;
  for (let d = garages.from + 30; d <= garages.to; d += 2) frontTrack = Math.max(frontTrack, side * trackOffsetAt(d).offset + garages.frontOffset);
  const garageFrontAt = (d: number): number => Math.max(ROAD_HALF_WIDTH, frontTrack - side * trackOffsetAt(d).offset);
  const halfWidthAt = (d: number): { left: number; right: number } => {
    const leftWide = smoothstep(straightFrom - 60, straightFrom, d);
    const rightWide = smoothstep(garages.from - 40, garages.from - 5, d) * (1 - smoothstep(garages.to + 5, garages.to + 40, d));
    const left = ROAD_HALF_WIDTH + (STRAIGHT_LEFT - ROAD_HALF_WIDTH) * leftWide;
    const right = ROAD_HALF_WIDTH + (garageFrontAt(Math.max(garages.from, Math.min(garages.to, d))) - ROAD_HALF_WIDTH) * rightWide;
    return side > 0 ? { left, right } : { left: right, right: left };
  };

  /** Distance of a world point beyond the track edge on the pit side, metres (negative = inside the track). */
  const beyondEdge = (x: number, z: number, hint: number): number => {
    const p = track.project(x, z, hint);
    const edge = side > 0 ? p.widthRight : p.widthLeft;
    return side * p.offset - edge;
  };

  const crossSection = (d: number): PitCrossSection | null => {
    const f = path.frameAt(d);
    const hw = halfWidthAt(d);
    // Track side is the pit lane's left when the pits are on the track's right.
    const trackSide = side > 0 ? hw.left : hw.right;
    const outerSide = side > 0 ? hw.right : hw.left;
    // Unit normal pointing away from the track (right-hand normal for side 1).
    const nx = f.tz * side;
    const nz = -f.tx * side;
    const inner = { x: f.x - nx * trackSide, z: f.z - nz * trackSide };
    const outer = { x: f.x + nx * outerSide, z: f.z + nz * outerSide };
    const hint = trackHintAt(d);
    const eOuter = beyondEdge(outer.x, outer.z, hint);
    if (eOuter <= 0.05) return null;
    let clipped = false;
    let innerPt = inner;
    if (beyondEdge(inner.x, inner.z, hint) < 0) {
      // Bisect along the cross-section for the track edge.
      let lo = 0;
      let hi = 1;
      for (let it = 0; it < 24; it++) {
        const m = (lo + hi) / 2;
        const e = beyondEdge(inner.x + (outer.x - inner.x) * m, inner.z + (outer.z - inner.z) * m, hint);
        if (e < 0) lo = m;
        else hi = m;
      }
      innerPt = { x: inner.x + (outer.x - inner.x) * hi, z: inner.z + (outer.z - inner.z) * hi };
      clipped = true;
    }
    return side > 0
      ? { d, left: innerPt, right: outer, leftOnTrackEdge: clipped }
      : { d, left: outer, right: innerPt, leftOnTrackEdge: clipped };
  };

  // Pit wall: along the track side of the lane, from where the lane's track-side edge comes inside
  // the barrier line (~20 m off the centreline) to just after the pit exit line.
  let wallFrom = straightFrom;
  for (let d = straightFrom - 60; d < straightFrom + 60; d += 1) {
    const hw = halfWidthAt(d);
    if (side * trackOffsetAt(d).offset - (side > 0 ? hw.left : hw.right) < 20) {
      wallFrom = d;
      break;
    }
  }
  const wallTo = Math.min(L, speedLimitEnd + 20);
  // Wall face offset table by lap distance (track-relative), sampled every 2 m of path.
  const wallTable: Array<{ s: number; face: number }> = [];
  for (let d = wallFrom; d <= wallTo; d += 2) {
    const cs = crossSection(d);
    if (!cs) continue;
    const pt = side > 0 ? cs.left : cs.right;
    const p = track.project(pt.x, pt.z, trackHintAt(d));
    const edge = side > 0 ? p.widthRight : p.widthLeft;
    wallTable.push({ s: p.s, face: Math.max(edge + WALL_GAP, side * p.offset + WALL_GAP) });
  }
  const wallFromS = wallTable[0]?.s ?? 0;
  const wallSpan = wallTable.length ? lapDelta(wallFromS, wallTable[wallTable.length - 1]!.s, lapL) : 0;
  const wallOffsetAt = (s: number): number | undefined => {
    if (!wallTable.length) return undefined;
    const rel = (((s - wallFromS) % lapL) + lapL) % lapL;
    if (rel > wallSpan) return undefined;
    let lo = 0;
    let hi = wallTable.length - 1;
    const relOf = (k: number): number => (((wallTable[k]!.s - wallFromS) % lapL) + lapL) % lapL;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (relOf(mid) <= rel) lo = mid;
      else hi = mid;
    }
    const a = wallTable[lo]!;
    const b = wallTable[hi]!;
    const t = (rel - relOf(lo)) / (relOf(hi) - relOf(lo) || 1);
    return Math.max(0, Math.min(1, t)) * (b.face - a.face) + a.face;
  };

  // Boxes: evenly along the building, in the working lane next to the garages.
  const G = garages.to - garages.from;
  const boxLen = ((BOXES_TO - BOXES_FROM) * G) / BOX_COUNT;
  const boxes: PitBox[] = [];
  for (let k = 0; k < BOX_COUNT; k++) {
    const d = garages.from + BOXES_FROM * G + boxLen * (k + 0.5);
    // Box centre 3 m out from the garage front (the crews work in front of the garage).
    boxes.push({ d, offset: side * (garageFrontAt(d) - 3.0), length: 6, width: 3, ours: k === OUR_BOX });
  }
  const boxAt = boxes[OUR_BOX]!.d;
  const boxOffset = side * boxes[OUR_BOX]!.offset;
  const boxLateralAt = (d: number): number => side * boxOffset * smoothstep(boxAt - 32, boxAt - 6, d) * (1 - smoothstep(boxAt + 4, boxAt + 30, d));

  // Entry commit window and the exit hand-back point.
  const entryS = meta.entryS;
  const commitToS = trackOffsetAt(COMMIT_LENGTH).s;
  const entryRoadLength = straightFrom;
  let handbackD = L;
  for (let d = wallTo; d <= L; d += 0.5) {
    const t = trackOffsetAt(d);
    if (side * t.offset <= edgeAt(d) - 1.5) {
      handbackD = d;
      break;
    }
  }

  const project = (x: number, z: number, hint?: number): PitProjection => {
    const p = path.project(x, z, hint);
    // A local search can lock on to the wrong stretch; fall back to a full search if far off.
    return hint !== undefined && p.distance > 30 ? path.project(x, z) : p;
  };

  // Bounding box of the path, for a cheap reject in inFootprint.
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  const reach = Math.max(STRAIGHT_LEFT, garageFrontAt(garages.from), garageFrontAt(garages.to), frontTrack, ROAD_HALF_WIDTH);
  const inFootprint = (x: number, z: number, margin = 0): boolean => {
    const r = reach + margin;
    if (x < minX - r || x > maxX + r || z < minZ - r || z > maxZ + r) return false;
    const p = path.project(x, z);
    if (p.d <= 0 && p.distance > 0.01) {
      // Before the start: the entry junction is on the track itself.
      return p.distance < ROAD_HALF_WIDTH + margin;
    }
    const hw = halfWidthAt(p.d);
    const lim = (p.offset >= 0 ? hw.right : hw.left) + margin;
    if (Math.abs(p.offset) > lim || p.distance > Math.max(hw.left, hw.right) + margin) return false;
    // The surface stops at the track's white line (see crossSection).
    return beyondEdge(x, z, trackHintAt(p.d)) >= 0;
  };

  return {
    path,
    length: L,
    entryS,
    exitS: meta.exitS,
    entryOffsetSide: side,
    speedLimitKmh: PIT_SPEED_LIMIT_KMH,
    speedLimitStart,
    speedLimitEnd,
    boxAt,
    boxOffset: boxOffset * side,
    boxes,
    garages: { ...garages, fromS, toS, trackOffset: frontTrack },
    garageFrontAt,
    wall: { from: wallFrom, to: wallTo, height: WALL_HEIGHT, thickness: WALL_THICKNESS },
    entry: { commitFromS: entryS, commitToS, roadLength: entryRoadLength },
    merge: { handbackD, handbackS: trackOffsetAt(handbackD).s, exitS: meta.exitS },
    heightAt: (d) => path.frameAt(d).y,
    poseAt: (d, lateral = 0) => {
      const f = path.frameAt(d);
      if (!lateral) return f;
      return { ...f, x: f.x + f.tz * lateral, z: f.z - f.tx * lateral };
    },
    boxLateralAt,
    project,
    halfWidthAt,
    trackOffsetAt,
    crossSection,
    isOnEntryRoad: (x, z) => {
      const p = path.project(x, z);
      if (p.d > entryRoadLength || p.distance > 0.01 + ROAD_HALF_WIDTH + 0.5) return false;
      return p.d > 0 || p.distance < 0.01;
    },
    commitsToPit: (s, offset) => {
      const rel = lapDelta(entryS, s, lapL);
      const span = lapDelta(entryS, commitToS, lapL);
      if (rel < 0 || rel > span) return false;
      // Path offset at this lap distance (the entry road's s increases monotonically).
      let lo = 0;
      let hi = COMMIT_LENGTH;
      for (let it = 0; it < 30; it++) {
        const mid = (lo + hi) / 2;
        if (lapDelta(entryS, trackOffsetAt(mid).s, lapL) < rel) lo = mid;
        else hi = mid;
      }
      const roadInner = side * trackOffsetAt(hi).offset - ROAD_HALF_WIDTH - COMMIT_TOLERANCE;
      const edge = side > 0 ? track.frameAt(s).widthRight : track.frameAt(s).widthLeft;
      const a = side * offset;
      return a > edge + COMMIT_EDGE_MARGIN && a >= roadInner;
    },
    wallOffsetAt,
    inFootprint,
  };
}

/** Sepang's pit lane (OpenStreetMap, see scripts/sepang-pit.mjs) on the Sepang track. */
export function sepangPitLane(track: Track): PitLane {
  return makePitLane(
    track,
    SEPANG_PIT_PATH.map(([x, z, y]) => ({ x, z, y })),
    SEPANG_PIT_META,
  );
}
